import React, { useState, useEffect } from "react";
import Select from "react-select";
import { HiArrowDownTray } from "react-icons/hi2";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import api from "../config/api";
import { toast } from "react-toastify";
import "./Reports.css";
import "../styles/buttons.css";
import { EMPTY_VALUE } from "../utils/display";
import { formSelectStyles } from "../utils/selectStyles";

interface ReportUser {
  id: number;
  name: string;
  email: string;
  type: "coder" | "supervisor";
  enterprise: string;
}

interface ActivityReport {
  userId: number;
  userName: string;
  period: "week" | "month" | "year";
  startDate: string;
  endDate: string;
  codings: {
    individual: number;
    collective: number;
    live: number;
    total: number;
  };
  supervisions: {
    total: number;
    byType: Record<string, number>;
  };
  games: number;
  contracts: {
    total: number;
    signed: number;
    pending: number;
  };
}

interface ReportFilter {
  userId: number;
  startDate?: string;
  endDate?: string;
  codingTypes?: string[];
  supervisionTypes?: string[];
  gameIds?: number[];
  contractStatus?: string[];
}

interface TimeSeriesData {
  period: string;
  codings: number;
  supervisions: number;
  average?: number;
}

const Reports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"user" | "analytics">("user");
  const [users, setUsers] = useState<ReportUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ReportUser | null>(null);
  const [period, setPeriod] = useState<"week" | "month" | "year">("month");
  const [report, setReport] = useState<ActivityReport | null>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [globalTimeSeriesData, setGlobalTimeSeriesData] = useState<
    TimeSeriesData[]
  >([]);
  const [loadingGlobalTimeSeries, setLoadingGlobalTimeSeries] = useState(false);
  const [globalPeriod, setGlobalPeriod] = useState<"week" | "month" | "year">(
    "month"
  );
  const [analyticsView, setAnalyticsView] = useState<"chart" | "table">(
    "chart"
  );
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    startDate: "",
    endDate: "",
    codingTypes: [] as string[],
    supervisionTypes: [] as string[],
    contractStatus: [] as string[],
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Load global analytics data when analytics tab is active
    if (activeTab === "analytics") {
      generateGlobalTimeSeriesData(globalPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, globalPeriod]);

  useEffect(() => {
    // Reload data when filters change
    if (activeTab === "analytics") {
      generateGlobalTimeSeriesData(globalPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/reporting/users");
      const transformedUsers = response.data.map((user: any) => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        type: user.type || "coder",
        enterprise: user.enterprise?.nom || EMPTY_VALUE,
      }));
      setUsers(transformedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedUser) {
      toast.warning("Veuillez sélectionner un utilisateur");
      return;
    }

    try {
      setGenerating(true);

      // Build filter object
      const filter: ReportFilter = {
        userId: selectedUser.id,
      };

      if (startDate) filter.startDate = startDate;
      if (endDate) filter.endDate = endDate;

      // Fetch activity data
      const response = await api.get(
        `/api/reporting/user/${selectedUser.id}/activity`,
        { params: { period, userType: selectedUser.type, ...filter } }
      );

      setReport(response.data);

      toast.success("Rapport généré avec succès!");
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setGenerating(false);
    }
  };

  const generateGlobalTimeSeriesData = async (
    timePeriod: "week" | "month" | "year"
  ) => {
    try {
      setLoadingGlobalTimeSeries(true);

      // Build filter parameters
      const filterParams: any = {
        period: timePeriod,
      };

      if (advancedFilters.startDate) {
        filterParams.startDate = advancedFilters.startDate;
      }
      if (advancedFilters.endDate) {
        filterParams.endDate = advancedFilters.endDate;
      }
      if (advancedFilters.codingTypes.length > 0) {
        filterParams.codingTypes = advancedFilters.codingTypes;
      }
      if (advancedFilters.supervisionTypes.length > 0) {
        filterParams.supervisionTypes = advancedFilters.supervisionTypes;
      }
      if (advancedFilters.contractStatus.length > 0) {
        filterParams.contractStatus = advancedFilters.contractStatus;
      }

      // Fetch global time series data from API
      const response = await api.get(`/api/reporting/global/timeseries`, {
        params: filterParams,
      });

      setGlobalTimeSeriesData(response.data);
    } catch (error) {
      console.error("Error generating global time series:", error);
      toast.error("Erreur lors de la génération des données analytiques");
    } finally {
      setLoadingGlobalTimeSeries(false);
    }
  };

  const exportReport = async (exportFormat: "pdf" | "csv" = "pdf") => {
    if (!selectedUser || !report) {
      toast.warning("Veuillez d'abord générer un rapport");
      return;
    }

    try {
      // Show loading toast
      const toastId = toast.loading(
        `Export en ${exportFormat.toUpperCase()} en cours...`
      );

      const response = await api.get(
        `/api/reporting/user/${selectedUser.id}/export`,
        {
          params: { period, format: exportFormat, userType: selectedUser.type },
          responseType: "blob",
          onDownloadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // Update toast with progress (optional)
            if (percentCompleted === 100) {
              toast.update(toastId, {
                render: `Export ${exportFormat.toUpperCase()} terminé à ${percentCompleted}%`,
                type: "info",
                isLoading: true,
              });
            }
          },
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Determine file extension based on format
      const ext = exportFormat === "csv" ? "csv" : "pdf";

      const fileName = `rapport_${selectedUser.name.replace(/ /g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.${ext}`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Update toast to success
      toast.update(toastId, {
        render: `Rapport exporté en ${exportFormat.toUpperCase()} avec succès!`,
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error(
        `Erreur lors de l'export du rapport en ${exportFormat.toUpperCase()}`
      );
    }
  };

  const clearAllFilters = () => {
    setAdvancedFilters({
      startDate: "",
      endDate: "",
      codingTypes: [],
      supervisionTypes: [],
      contractStatus: [],
    });
    toast.info("Tous les filtres ont été effacés");
  };

  const handleFilterChange = (filterName: string, value: any) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des utilisateurs...</p>
      </div>
    );
  }

  const userOptions = users.map((user) => ({
    value: `${user.type}-${user.id}`, // Unique key combining type and id
    label: `${user.name} (${user.type === "coder" ? "Codeur" : "Superviseur"})`,
    ...user,
  }));

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1 className="page-title">Rapports d'activité</h1>
      </div>

      {/* Tabs Navigation */}
      <div className="reports-tabs">
        <button
          className={`reports-tab ${activeTab === "user" ? "active" : ""}`}
          onClick={() => setActiveTab("user")}
        >
          Rapports par utilisateur
        </button>
        <button
          className={`reports-tab ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          Analytiques globales
        </button>
      </div>

      <div className="reports-container">
        {/* User Reports Tab */}
        {activeTab === "user" && (
          <div>
            <div className="reports-section">
              <div className="selection-card">
                <h2>Sélection d'utilisateur</h2>

                <div className="form-group">
                  <label>Utilisateur</label>
                  <Select
                    value={
                      selectedUser
                        ? {
                            value: `${selectedUser.type}-${selectedUser.id}`,
                            label: `${selectedUser.name} (${
                              selectedUser.type === "coder"
                                ? "Codeur"
                                : "Superviseur"
                            })`,
                          }
                        : null
                    }
                    onChange={(option) => {
                      const [type, id] =
                        (option?.value as string)?.split("-") || [];
                      const selected = users.find(
                        (u) => u.type === type && u.id === parseInt(id)
                      );
                      setSelectedUser(selected || null);
                      setReport(null);
                    }}
                    options={userOptions}
                    styles={formSelectStyles}
                    placeholder="Sélectionner un utilisateur..."
                    isSearchable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                {selectedUser && (
                  <div className="user-details">
                    <div className="detail-card">
                      <h3>Détails de l'utilisateur</h3>
                      <div className="details-content">
                        <div className="detail-row">
                          <span className="label">Nom :</span>
                          <span className="value">{selectedUser.name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Email :</span>
                          <span className="value">{selectedUser.email}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Type :</span>
                          <span className="value">
                            {selectedUser.type === "coder"
                              ? "Codeur"
                              : "Superviseur"}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Entreprise :</span>
                          <span className="value">
                            {selectedUser.enterprise}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {selectedUser && (
                <div className="filters-card">
                  <h2>Paramètres du rapport</h2>

                  <div className="filters-grid">
                    <div className="form-group">
                      <label>Période</label>
                      <select
                        value={period}
                        onChange={(e) =>
                          setPeriod(e.target.value as "week" | "month" | "year")
                        }
                        className="filter-select"
                      >
                        <option value="week">Cette semaine</option>
                        <option value="month">Ce mois</option>
                        <option value="year">Cette année</option>
                      </select>
                    </div>{" "}
                    <div className="form-group">
                      <label>Date de début (optionnel)</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Date de fin (optionnel)</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                  </div>

                  <div className="actions-group">
                    <button
                      onClick={generateReport}
                      disabled={generating}
                      className="btn-primary"
                    >
                      {generating
                        ? "Génération en cours..."
                        : "Générer le rapport"}
                    </button>
                    {report && (
                      <div className="export-buttons-group">
                        <button
                          onClick={() => exportReport("pdf")}
                          className="btn-secondary"
                          title="Exporter le rapport en PDF"
                        >
                          <HiArrowDownTray /> Exporter PDF
                        </button>
                        <button
                          onClick={() => exportReport("csv")}
                          className="btn-secondary"
                          title="Exporter le rapport en CSV"
                        >
                          <HiArrowDownTray /> Exporter CSV
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {report && (
                <div className="report-section">
                  <div className="report-container">
                    <div className="report-header">
                      <h2>Rapport d'activité</h2>
                      <div className="report-period">
                        {report.startDate} - {report.endDate}
                      </div>
                    </div>

                    <div className="reports-stats-grid">
                      {/* Codings Stats */}
                      <div className="reports-stat-card coding-card">
                        <h3>Codages</h3>
                        <div className="reports-stat-content">
                          <div className="reports-stat-row">
                            <span className="reports-stat-label">Total :</span>
                            <span className="reports-stat-value">
                              {report.codings.total}
                            </span>
                          </div>
                          <div className="reports-stat-breakdown">
                            <div className="reports-breakdown-item">
                              <span>Individuels :</span>
                              <span className="number">
                                {report.codings.individual}
                              </span>
                            </div>
                            <div className="reports-breakdown-item">
                              <span>Collectifs :</span>
                              <span className="number">
                                {report.codings.collective}
                              </span>
                            </div>
                            <div className="reports-breakdown-item">
                              <span>Live :</span>
                              <span className="number">
                                {report.codings.live}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Supervisions Stats */}
                      <div className="reports-stat-card supervision-card">
                        <h3>Supervisions</h3>
                        <div className="reports-stat-content">
                          <div className="reports-stat-row">
                            <span className="reports-stat-label">Total :</span>
                            <span className="reports-stat-value">
                              {report.supervisions.total}
                            </span>
                          </div>
                          <div className="reports-stat-breakdown">
                            {Object.entries(report.supervisions.byType).map(
                              ([type, count]) => (
                                <div
                                  key={type}
                                  className="reports-breakdown-item"
                                >
                                  <span>{type}:</span>
                                  <span className="number">
                                    {count as number}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Games Stats */}
                      <div className="reports-stat-card games-card">
                        <h3>Matchs</h3>
                        <div className="reports-stat-content">
                          <div className="reports-big-stat">{report.games}</div>
                          <p>Matchs traités</p>
                        </div>
                      </div>

                      {/* Contracts Stats */}
                      <div className="reports-stat-card contracts-card">
                        <h3>Contrats</h3>
                        <div className="reports-stat-content">
                          <div className="reports-stat-row">
                            <span className="reports-stat-label">Total :</span>
                            <span className="reports-stat-value">
                              {report.contracts.total}
                            </span>
                          </div>
                          <div className="reports-stat-breakdown">
                            <div className="reports-breakdown-item">
                              <span>Signés :</span>
                              <span className="number">
                                {report.contracts.signed}
                              </span>
                            </div>
                            <div className="reports-breakdown-item">
                              <span>En attente :</span>
                              <span className="number">
                                {report.contracts.pending}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bars */}
                    <div className="progress-section">
                      <h3>Progression des tâches</h3>

                      <div className="progress-item">
                        <div className="progress-header">
                          <span>Contrats signés</span>
                          <span className="progress-percentage">
                            {report.contracts.total > 0
                              ? Math.round(
                                  (report.contracts.signed /
                                    report.contracts.total) *
                                    100
                                )
                              : 0}
                            %
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${
                                report.contracts.total > 0
                                  ? (report.contracts.signed /
                                      report.contracts.total) *
                                    100
                                  : 0
                              }%`,
                            }}
                          ></div>
                        </div>
                      </div>

                      <div className="progress-item">
                        <div className="progress-header">
                          <span>Codages par rapport au total</span>
                          <span className="progress-percentage">
                            {report.codings.total}
                          </span>
                        </div>
                        <div className="progress-breakdown">
                          {report.codings.total > 0 && (
                            <>
                              <div className="progress-segment individual">
                                <div
                                  className="segment-fill"
                                  style={{
                                    width: `${
                                      (report.codings.individual /
                                        report.codings.total) *
                                      100
                                    }%`,
                                  }}
                                  title="Individuels"
                                ></div>
                              </div>
                              <div className="progress-segment collective">
                                <div
                                  className="segment-fill"
                                  style={{
                                    width: `${
                                      (report.codings.collective /
                                        report.codings.total) *
                                      100
                                    }%`,
                                  }}
                                  title="Collectifs"
                                ></div>
                              </div>
                              <div className="progress-segment live">
                                <div
                                  className="segment-fill"
                                  style={{
                                    width: `${
                                      (report.codings.live /
                                        report.codings.total) *
                                      100
                                    }%`,
                                  }}
                                  title="Live"
                                ></div>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="legend">
                          <span className="legend-item individual">
                            Individuels
                          </span>
                          <span className="legend-item collective">
                            Collectifs
                          </span>
                          <span className="legend-item live">Live</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="analytics-section">
            <div className="analytics-card">
              <h2>Analytiques globales</h2>
              <p className="analytics-description">
                Consultez l'activité globale du système (tous les codages et
                supervisions)
              </p>

              <div className="analytics-controls">
                <div className="form-group">
                  <label>Période</label>
                  <select
                    value={globalPeriod}
                    onChange={(e) =>
                      setGlobalPeriod(
                        e.target.value as "week" | "month" | "year"
                      )
                    }
                    className="filter-select"
                  >
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                    <option value="year">Cette année</option>
                  </select>
                </div>

                <div className="view-toggle">
                  <button
                    className={`toggle-btn ${
                      analyticsView === "chart" ? "active" : ""
                    }`}
                    onClick={() => setAnalyticsView("chart")}
                    title="Vue graphique"
                  >
                    Graphique
                  </button>
                  <button
                    className={`toggle-btn ${
                      analyticsView === "table" ? "active" : ""
                    }`}
                    onClick={() => setAnalyticsView("table")}
                    title="Vue tableau"
                  >
                    Tableau
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="filters-panel">
              <button
                className="filters-toggle"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                Filtres avancés {showAdvancedFilters ? "▲" : "▼"}
              </button>

              {showAdvancedFilters && (
                <div className="advanced-filters">
                  <div className="filters-grid">
                    {/* Date Range */}
                    <div className="filter-group">
                      <label>Date de début</label>
                      <input
                        type="date"
                        value={advancedFilters.startDate}
                        onChange={(e) =>
                          handleFilterChange("startDate", e.target.value)
                        }
                        className="filter-input"
                      />
                    </div>

                    <div className="filter-group">
                      <label>Date de fin</label>
                      <input
                        type="date"
                        value={advancedFilters.endDate}
                        onChange={(e) =>
                          handleFilterChange("endDate", e.target.value)
                        }
                        className="filter-input"
                      />
                    </div>

                    {/* Coding Types */}
                    <div className="filter-group">
                      <label>Types de codage</label>
                      <div className="checkbox-group">
                        {[
                          "Individual",
                          "Individual_v2",
                          "Software Collective Advanced",
                          "Live",
                        ].map((type) => (
                          <label key={type} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={advancedFilters.codingTypes.includes(
                                type
                              )}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...advancedFilters.codingTypes, type]
                                  : advancedFilters.codingTypes.filter(
                                      (t) => t !== type
                                    );
                                handleFilterChange("codingTypes", newTypes);
                              }}
                            />
                            {type === "Individual"
                              ? "Individuel"
                              : type === "Individual_v2"
                              ? "Individuel v2"
                              : type === "Software Collective Advanced"
                              ? "Collectif"
                              : "En direct"}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Supervision Types */}
                    <div className="filter-group">
                      <label>Types de supervision</label>
                      <div className="checkbox-group">
                        {["live", "remote", "on_site"].map((type) => (
                          <label key={type} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={advancedFilters.supervisionTypes.includes(
                                type
                              )}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...advancedFilters.supervisionTypes, type]
                                  : advancedFilters.supervisionTypes.filter(
                                      (t) => t !== type
                                    );
                                handleFilterChange(
                                  "supervisionTypes",
                                  newTypes
                                );
                              }}
                            />
                            {type === "live"
                              ? "Live"
                              : type === "remote"
                              ? "Distant"
                              : "Sur site"}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Contract Status */}
                    <div className="filter-group">
                      <label>Statut de contrat</label>
                      <div className="checkbox-group">
                        {["pending", "signed", "rejected"].map((status) => (
                          <label key={status} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={advancedFilters.contractStatus.includes(
                                status
                              )}
                              onChange={(e) => {
                                const newStatus = e.target.checked
                                  ? [...advancedFilters.contractStatus, status]
                                  : advancedFilters.contractStatus.filter(
                                      (s) => s !== status
                                    );
                                handleFilterChange("contractStatus", newStatus);
                              }}
                            />
                            {status === "pending"
                              ? "En attente"
                              : status === "signed"
                              ? "Signé"
                              : "Rejeté"}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="filters-actions">
                    <button
                      onClick={clearAllFilters}
                      className="btn-clear-filters"
                    >
                      ✕ Effacer tous les filtres
                    </button>
                  </div>
                </div>
              )}
            </div>

            {loadingGlobalTimeSeries ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Chargement des données analytiques...</p>
              </div>
            ) : globalTimeSeriesData.length > 0 ? (
              <div className="time-series-section">
                <div className="time-series-container">
                  <div className="time-series-header">
                    <h2>Activité du système</h2>
                    <p>Analyse des performances globales</p>
                  </div>

                  {analyticsView === "chart" ? (
                    <div className="time-series-content">
                      {/* Codings Chart */}
                      <div className="chart-wrapper">
                        <h3>
                          Codages par{" "}
                          {globalPeriod === "week"
                            ? "Jour"
                            : globalPeriod === "month"
                            ? "Jour"
                            : "Mois"}
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={globalTimeSeriesData}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="rgba(255,255,255,0.1)"
                            />
                            <XAxis
                              dataKey="period"
                              stroke="rgba(255,255,255,0.7)"
                            />
                            <YAxis stroke="rgba(255,255,255,0.7)" />
                            <Tooltip
                              contentStyle={{
                                background: "rgb(26, 20, 51)",
                                border: "1px solid rgba(255,255,255,0.2)",
                              }}
                              labelStyle={{ color: "white" }}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="codings"
                              stroke="#3b82f6"
                              name="Codages"
                              strokeWidth={2}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Supervisions Chart */}
                      <div className="chart-wrapper">
                        <h3>
                          Supervisions par{" "}
                          {globalPeriod === "week"
                            ? "Jour"
                            : globalPeriod === "month"
                            ? "Jour"
                            : "Mois"}
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={globalTimeSeriesData}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="rgba(255,255,255,0.1)"
                            />
                            <XAxis
                              dataKey="period"
                              stroke="rgba(255,255,255,0.7)"
                            />
                            <YAxis stroke="rgba(255,255,255,0.7)" />
                            <Tooltip
                              contentStyle={{
                                background: "rgb(26, 20, 51)",
                                border: "1px solid rgba(255,255,255,0.2)",
                              }}
                              labelStyle={{ color: "white" }}
                            />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="supervisions"
                              stroke="#22c55e"
                              name="Supervisions"
                              strokeWidth={2}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Comparison Chart */}
                      <div className="chart-wrapper">
                        <h3>Comparaison codages vs supervisions</h3>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={globalTimeSeriesData}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="rgba(255,255,255,0.1)"
                            />
                            <XAxis
                              dataKey="period"
                              stroke="rgba(255,255,255,0.7)"
                            />
                            <YAxis stroke="rgba(255,255,255,0.7)" />
                            <Tooltip
                              contentStyle={{
                                background: "rgb(26, 20, 51)",
                                border: "1px solid rgba(255,255,255,0.2)",
                              }}
                              labelStyle={{ color: "white" }}
                            />
                            <Legend />
                            <Bar
                              dataKey="codings"
                              fill="#3b82f6"
                              name="Codages"
                            />
                            <Bar
                              dataKey="supervisions"
                              fill="#22c55e"
                              name="Supervisions"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="analytics-table-view">
                      <table className="analytics-table">
                        <thead>
                          <tr>
                            <th>Période</th>
                            <th>Codages</th>
                            <th>Supervisions</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {globalTimeSeriesData.map((row, index) => (
                            <tr key={index}>
                              <td>{row.period}</td>
                              <td className="text-center">{row.codings}</td>
                              <td className="text-center">
                                {row.supervisions}
                              </td>
                              <td className="text-center font-weight-bold">
                                {row.codings + row.supervisions}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Statistics */}
                  <div className="time-series-stats">
                    <div className="time-stat">
                      <h4>Total codages</h4>
                      <div className="time-stat-value">
                        {globalTimeSeriesData.reduce(
                          (sum, d) => sum + d.codings,
                          0
                        )}
                      </div>
                    </div>
                    <div className="time-stat">
                      <h4>Moyenne codages</h4>
                      <div className="time-stat-value">
                        {Math.round(
                          globalTimeSeriesData.reduce(
                            (sum, d) => sum + d.codings,
                            0
                          ) / globalTimeSeriesData.length
                        )}
                      </div>
                    </div>
                    <div className="time-stat">
                      <h4>Total supervisions</h4>
                      <div className="time-stat-value">
                        {globalTimeSeriesData.reduce(
                          (sum, d) => sum + d.supervisions,
                          0
                        )}
                      </div>
                    </div>
                    <div className="time-stat">
                      <h4>Moyenne supervisions</h4>
                      <div className="time-stat-value">
                        {Math.round(
                          globalTimeSeriesData.reduce(
                            (sum, d) => sum + d.supervisions,
                            0
                          ) / globalTimeSeriesData.length
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-data-state">
                <p>Aucune donnée analytique disponible pour cette période</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
